import React, { useRef } from 'react';
import { useNavigateAndScroll } from '../../Hooks/useNavigateAndScroll';
import PatientFormsList from '../PatientFormsList';

const ExpectingParents = () => {
  const barRef = useRef(null);
  const navigateAndScroll = useNavigateAndScroll();
  const expectantParentItems = [
    { label: 'New patient registration',   link: `${process.env.REACT_APP_ASSET_BUCKET_URL}/newpatient_registration.pdf` },
    { label: 'Newborn Questionnaire',       link: `${process.env.REACT_APP_ASSET_BUCKET_URL}/newborn_patient_questionnaire.pdf` },
    { label: 'Privacy policy and consent',  link: `${process.env.REACT_APP_ASSET_BUCKET_URL}/noticeforprivacypractices.pdf` },
  ];
  const infoCardsData = {
    expectantParents: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/expectant_parents.webp",
      infoTitle: "Expectant Parents",
      infoText: "Comprehensive prenatal care, guidance, and support for expecting mothers and families preparing for their new arrival.",
      path: "/services/expectant-parents"
    },
    wellChecks: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Well+Check.webp",
      infoTitle: "Well Checks", 
      infoText: "Regular preventive care appointments to monitor your child's growth, development, and overall health at every stage.",
      path: "/services/well-checks"
    },
    sameDaySickVisits: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/sick+same+day.webp",
      infoTitle: "Same Day Sick Visits",
      infoText: "Prompt medical attention for acute illnesses and urgent health concerns when your child needs immediate care.",
      path: "/services/same-day-sick-visits"
    },
    functionalMedicine: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/Functional+Medicine.webp",
      infoTitle: "Functional Medicine",
      infoText: "Holistic approach to healthcare focusing on identifying and addressing the root causes of health issues for optimal wellness.",
      path: "/services/functional-medicine"
    },
    commonMedicalConditions: {
      image: "https://cipa-website-assets.s3.us-east-2.amazonaws.com/site-photos/common+medical+conditions.webp",
      infoTitle: "Common Medical Conditions",
      infoText: "Information and treatment options for frequently encountered pediatric and family health conditions and concerns.",
      path: "/services/common-medical-conditions"
    }
  };
  return (
    <div>
    <div className="relative w-auto z-1 mt-[-70px]">
          <img
            className="w-full h-auto rounded-lg shadow-lg mt-2"
            src="https://cipa-website-forms.s3.us-east-2.amazonaws.com/expectant+parents_InPixio.webp"
            alt="Mother and child meeting a doctor"
          />
        </div>
    <div ref={barRef} className="sticky top-0 flex justify-around bg-custom-blue text-white z-50">
        {Object.values(infoCardsData).map((card) => (
          <button
            key={card.infoTitle}
            className="p-1 text-sm cursor-pointer grow-text"
            onClick={() => navigateAndScroll(card.path, card.infoTitle)}
          >
            {card.infoTitle}
          </button>
        ))}
      </div>
    <div className="w-full px-4 py-8 sm:px-6 lg:px-64">
      <h2 className="text-4xl font-bold text-gray-800 mb-2">Congratulations, Expecting Parents!</h2>
      <hr className="border-t-2 border-blue-500 mb-6" />

      <div className="space-y-6 text-gray-600">
        <section>
          <p className="mb-4">
            The arrival of your baby is one of life’s most joyful and transformative experiences. As you prepare to welcome your little one into the world, we understand how crucial it is to create a nurturing environment where both you and your child can thrive.
          </p>
          <p className="mb-4">
            Choosing the right pediatrician to care for your baby is an important decision. At <span className="font-bold">Central Illinois Pediatric Associates</span>, we share in your excitement and are honored to partner with you in supporting your child’s health, growth, and development.
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Getting to Know Us</h3>
          <p className="mb-4">
            We are here to answer any questions you may have about our practice and the care we provide. If you'd like to meet with us before your baby’s arrival, please feel free to contact our office to schedule a prenatal <span className="font-bold">“get-acquainted”</span> visit. Our goal is to ensure that you feel confident, informed, and fully prepared as you enter this exciting new chapter.
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">After Baby’s Arrival</h3>
          <p className="mb-4">
            When your baby is born at one of our affiliated hospitals, simply notify your obstetrician and the hospital staff that you have chosen <span className="font-bold">Central Illinois Pediatric Associates</span> as your pediatric provider. The hospital staff will then notify our office. One of our providers will come to check on your baby during your stay at the hospital.
          </p>
          <p className="mb-4">
            After discharge, please contact our office to schedule your baby’s first visit.
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Newborn Care</h3>
          <p className="mb-4">
            Congratulations on the arrival of your baby! Whether you’re a first-time parent or have experience, we understand you may have many questions about caring for your newborn. While it’s natural to feel nervous about dressing, changing, and handling your baby, rest assured—you’ve got this! The pediatric team at <span className="font-bold">Central Illinois Pediatric Associates</span> is here to guide and support you every step of the way, from wellness checkups to offering helpful recommendations for your baby's care.
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Handling Your Newborn</h3>
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li>Wash your hands thoroughly before handling your baby.</li>
            <li>Support your baby’s head and neck at all times.</li>
            <li>Be gentle when holding or moving your baby.</li>
            <li>Never shake your baby, even if they are hard to wake.</li>
          </ul>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Bathing Your Newborn</h3>
          <p className="mb-4">
            For the first few weeks, you will need to sponge-bathe your baby until the umbilical cord stump falls off and the area heals. If your baby was circumcised, you’ll want to wait for that area to heal as well before beginning regular baths. Once you can begin full baths, you’ll only need to bathe your newborn about 2-3 times a week to avoid drying out their delicate skin. Use mild, fragrance-free baby soaps and shampoos, along with soft washcloths and towels. When you transition to full baths, an infant tub will be helpful.
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Breastfeeding</h3>
          <p className="mb-4">
            If you plan to breastfeed, it's important to begin as soon as your baby is born in the hospital. Don’t worry if it takes a little time to get the hang of it—that’s completely normal. If you experience challenges, we can connect you with a lactation consultant to help make the process easier.
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">When to Contact Your Pediatrician</h3>
          <p className="mb-4">
            Along with routine wellness checkups, our pediatric team is here to assist you whenever you need support. If your newborn shows any of the following symptoms, don’t hesitate to contact us:
          </p>
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li>A fever above 100.4°F in babies under 2 months old</li>
            <li>Difficulty or refusal to eat</li>
            <li>Watery stools or diarrhea</li>
            <li>Extreme sleepiness or unresponsiveness</li>
            <li>Excessive crying that cannot be soothed</li>
            <li>A red rash covering the body</li>
            <li>Signs of infection around the umbilical cord (redness, swelling)</li>
            <li>A swollen stomach</li>
          </ul>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Schedule Your Baby’s First Visit</h3>
          <PatientFormsList items={expectantParentItems}/>
          <p className="mb-4">
            If you have any questions or are looking for a pediatrician for your newborn, call us at <span className="font-bold">(309) 663-1011</span> to schedule your little one’s first visit. We look forward to partnering with you in your child’s health journey!
          </p>
        </section>

        <section>
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Additional Resources</h3>
          <ul className="list-disc list-outside ml-5 space-y-2">
            <li><span className="font-bold">About Us:</span> Learn more about our practice, our dedicated team of pediatricians and staff, and the comprehensive services we offer.</li>
            <li><span className="font-bold">Resources:</span> Download our newborn care book by clicking <a href="#" className="text-blue-600 hover:text-blue-800">here</a> for helpful tips and guidance to support you in the early days of parenthood.</li>
          </ul>
        </section>

        <p className="mt-6 text-gray-600 font-semibold">
          Once again, congratulations on this exciting milestone. We look forward to being part of your journey and providing exceptional care for your growing family!
        </p>
      </div>
    </div>
    </div>
  );
};

export default ExpectingParents;
